import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, limit, onSnapshot, updateDoc, arrayRemove, arrayUnion, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, storage } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import ListingItem from '../components/ListingItem';
import { FaPhone, FaWhatsapp, FaImages, FaUserCheck, FaUserPlus, FaDownload, FaCode, FaMobile, FaGlobe, FaTimes } from 'react-icons/fa';
import { IoLocationSharp } from "react-icons/io5";
import { GoHeartFill } from 'react-icons/go';
import { BiSolidMessageSquareDetail } from 'react-icons/bi';
import { IoBookmark } from 'react-icons/io5';
import { FaShare } from 'react-icons/fa';
import { AiFillMessage } from 'react-icons/ai';
import { SiSimpleanalytics } from "react-icons/si";
import { MdCloudDownload, MdIntegrationInstructions } from 'react-icons/md';
import { BsFillFileEarmarkCodeFill } from 'react-icons/bs';
import Modal from 'react-modal';
import CommentSection from '../components/CommentSection';
import VerificationBadge from '../components/VerificationBadge';
import { useFollow } from '../utils/FollowContext';

Modal.setAppElement('#root');

export default function ListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [similarListings, setSimilarListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [saveCount, setSaveCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [likeAnimation, setLikeAnimation] = useState(false);
  const [saveAnimation, setSaveAnimation] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPurchaseMessage, setShowPurchaseMessage] = useState(false);
  const { isFollowing, shouldFollowBack, toggleFollow, isInitialized } = useFollow();

  // Get category icon
  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Mobile Apps': return <FaMobile className="text-blue-500" />;
      case 'Web Apps': return <FaGlobe className="text-green-500" />;
      case 'Brand Startups': return <FaCode className="text-purple-500" />;
      case 'UI/UX Kits': return <BsFillFileEarmarkCodeFill className="text-pink-500" />;
      case 'Backend Systems': return <FaCode className="text-orange-500" />;
      case 'AI & Automation Tools': return <MdIntegrationInstructions className="text-cyan-500" />;
      case 'Programming Courses': return <FaCode className="text-yellow-500" />;
      case 'Graphic designs': return <FaImages className="text-indigo-500" />;
      default: return <FaCode className="text-gray-500" />;
    }
  };

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const listingRef = doc(db, 'listings', id);
        const listingSnap = await getDoc(listingRef);
        if (listingSnap.exists()) {
          const data = listingSnap.data();
          setListing({ id: listingSnap.id, ...data });
          
          // Set interaction states based on user authentication
          if (auth.currentUser) {
            setIsLiked(data.likedBy?.includes(auth.currentUser?.uid) || false);
            setIsSaved(data.savedBy?.includes(auth.currentUser?.uid) || false);
          } else {
            setIsLiked(false);
            setIsSaved(false);
          }
          
          setLikeCount(data.likes || 0);
          setCommentCount(data.commentCount || 0);
          setSaveCount(data.savedBy?.length || 0);
          setShareCount(data.shareCount || 0);
          
          // Increment view count only for authenticated users who are not the owner
          if (auth.currentUser && auth.currentUser.uid !== data.userRef) {
            await updateDoc(listingRef, {
              viewCount: (data.viewCount || 0) + 1
            });
            setViewCount((data.viewCount || 0) + 1);
          } else {
            // For unauthenticated users or owners, just show the current view count
            setViewCount(data.viewCount || 0);
          }

          // Fetch seller data
          const sellerRef = doc(db, 'users', data.userRef);
          const sellerSnap = await getDoc(sellerRef);
          if (sellerSnap.exists()) {
            setSeller({ id: sellerSnap.id, ...sellerSnap.data() });
          }

          // Fetch similar listings with error handling
          const listingsRef = collection(db, 'listings');
          const q = query(
            listingsRef,
            where('category', '==', data.category),
            orderBy('timestamp', 'desc'),
            limit(6)
          );
          
          const unsubscribe = onSnapshot(q, (snapshot) => {
            const listingsData = [];
            snapshot.forEach((doc) => {
              if (doc.id !== id) {
                listingsData.push({ id: doc.id, ...doc.data() });
              }
            });
            setSimilarListings(listingsData);
          }, (error) => {
            // Silently handle permission errors - they won't affect the user experience
            if (error.code !== 'permission-denied') {
              console.error("Error fetching similar listings:", error);
            }
            setSimilarListings([]); // Set empty array on error
          });

          // Real-time comment count update with auth check
          let unsubscribeComments = () => {};
          
          const commentsRef = collection(db, `listings/${id}/comments`);
          const commentsQuery = query(commentsRef, orderBy('timestamp', 'asc'));
          
          unsubscribeComments = onSnapshot(commentsQuery, (snapshot) => {
            const commentsCount = snapshot.size;
            setCommentCount(commentsCount);
            // Only try to update if user is authenticated
            if (auth.currentUser) {
              updateDoc(listingRef, { commentCount: commentsCount }).catch(err => {
                // Silently fail - not critical
              });
            }
          }, (error) => {
            // Silently handle permission errors
            if (error.code !== 'permission-denied') {
              console.error("Error fetching comments count:", error);
              toast.error("Failed to load comments count");
            }
            // Use the existing comment count from the listing data
            setCommentCount(data.commentCount || 0);
          });

          return () => {
            unsubscribe();
            unsubscribeComments();
          };
        } else {
          toast.error('Listing not found');
          navigate('/categories');
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
        // Don't show error toast for permission issues on unauthenticated views
        if (error.code !== 'permission-denied') {
          toast.error('Failed to load listing');
        }
        navigate('/categories');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchListing();
    }
  }, [id, navigate, auth.currentUser]);

  // Helper function to create a notification
  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!auth.currentUser || auth.currentUser.uid === recipientId) return; // Don't notify self

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: auth.currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Handle follow/unfollow
  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(seller?.id);
      await toggleFollow(seller?.id);
      if (!wasFollowing) {
        // Notify only on follow, not unfollow
        const message = `started following you.`;
        await createNotification('follow', seller?.id, message);
      }
    } catch (error) {
      console.error('Error handling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Determine follow button text and style
  const getFollowButtonState = () => {
    if (!isInitialized || !seller) {
      return {
        text: '...',
        className: 'bg-gray-500 text-white',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    
    if (isFollowing(seller.id)) {
      return {
        text: 'Following',
        className: 'bg-black text-white hover:bg-gray-800',
        icon: <FaUserCheck className="mr-1" />
      };
    }
    if (shouldFollowBack(seller.id)) {
      return {
        text: 'Follow Back',
        className: 'bg-blue-500 text-white hover:bg-blue-600',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    return {
      text: 'Follow',
      className: 'bg-white text-black hover:bg-gray-100',
      icon: <FaUserPlus className="mr-1" />
    };
  };

  const followState = getFollowButtonState();

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp?.toDate) return 'Just now';
    const now = new Date();
    const listingDate = timestamp.toDate();
    const diffInSeconds = Math.floor((now - listingDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Format price with commas
  const formatPrice = (price) => {
    if (price === 'Negotiable') return price;
    return Number(price).toLocaleString();
  };

  // Handle like action
  const handleLike = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      toast.error('Please sign in to like listings');
      navigate('/welcome');
      return;
    }

    const listingRef = doc(db, 'listings', id);
    const userId = auth.currentUser.uid;

    try {
      setLikeAnimation(true);
      setTimeout(() => setLikeAnimation(false), 1000);

      if (isLiked) {
        await updateDoc(listingRef, {
          likedBy: arrayRemove(userId),
          likes: likeCount - 1,
        });
        setLikeCount(likeCount - 1);
        setIsLiked(false);
      } else {
        await updateDoc(listingRef, {
          likedBy: arrayUnion(userId),
          likes: likeCount + 1,
        });
        setLikeCount(likeCount + 1);
        setIsLiked(true);
      }
    } catch (error) {
      toast.error('Error updating like status');
      console.error('Like error:', error);
    }
  };

  // Handle save action
  const handleSave = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      toast.error('Please sign in to save listings');
      navigate('/welcome');
      return;
    }

    const listingRef = doc(db, 'listings', id);
    const userId = auth.currentUser.uid;

    try {
      setSaveAnimation(true);
      setTimeout(() => setSaveAnimation(false), 1000);

      if (isSaved) {
        await updateDoc(listingRef, {
          savedBy: arrayRemove(userId),
        });
        setSaveCount(saveCount - 1);
        setIsSaved(false);
      } else {
        await updateDoc(listingRef, {
          savedBy: arrayUnion(userId),
        });
        setSaveCount(saveCount + 1);
        setIsSaved(true);
      }
    } catch (error) {
      toast.error('Error updating save status');
      console.error('Save error:', error);
    }
  };

  // Handle comment action
  const handleComment = (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      toast.error('Please sign in to comment');
      navigate('/welcome');
      return;
    }
    setShowComments(!showComments);
  };

  // Update comment count when a new comment is added
  const handleNewComment = () => {
    setCommentCount(prevCount => prevCount + 1);
  };

  // Handle share action
  const handleShare = async (e) => {
    e.stopPropagation();

    const shareUrl = `${window.location.origin}/listing/${id}`;
    const shareData = {
      title: listing.title,
      text: `Check out "${listing.title}" on SourceCodey Marketplace!`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        if (auth.currentUser) {
          await updateDoc(doc(db, 'listings', id), {
            shareCount: shareCount + 1,
          });
        }
        setShareCount(shareCount + 1);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Listing link copied to clipboard! Share it manually.');
      }
    } catch (error) {
      console.error('Error sharing listing:', error);
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Listing link copied to clipboard! Share it manually.');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
      }
    }
  };

  // Handle download/buy action
  const handleProductAction = async () => {
    if (!auth.currentUser) {
      toast.error('Please sign in to access this product');
      navigate('/welcome');
      return;
    }

    // Check if it's a free product (price = 0)
    const isFree = listing.price === 0 || listing.price === '0' || listing.price === 0.0;

    if (isFree) {
      // Free product - proceed with download
      if (!listing.zipFileUrl) {
        toast.error('No downloadable file available');
        return;
      }

      setDownloading(true);
      try {
        const link = document.createElement('a');
        link.href = listing.zipFileUrl;
        link.download = listing.zipFileName || 'source-code.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started!');
      } catch (error) {
        console.error('Error downloading file:', error);
        toast.error('Failed to download file');
      } finally {
        setDownloading(false);
      }
    } else {
      // Paid product - show purchase message
      setShowPurchaseMessage(true);
    }
  };

  // Handle call action
  const handleCall = () => {
    if (!listing.contactPhone) {
      toast.error('Contact phone not available');
      return;
    }
    window.location.href = `tel:${listing.contactPhone}`;
  };

  // Handle WhatsApp action
  const handleWhatsApp = () => {
    if (!listing.contactPhone) {
      toast.error('Contact phone not available');
      return;
    }
    const message = `Hello, I'm interested in your digital product "${listing.title}" on SourceCodey Marketplace.`;
    const url = `https://wa.me/${listing.contactPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Handle SourceChat action
  const handleSourceChat = () => {
    if (!auth.currentUser) {
      toast.error('Please sign in to start a chat');
      navigate('/welcome');
      return;
    }
    toast.info('Not yet available in your region!');
  };

  // Handle seller profile navigation
  const handleSellerProfile = () => {
    if (!auth.currentUser) {
      toast.error('Please sign in to view profile');
      navigate('/welcome');
      return;
    }
    navigate(`/profile/${listing.userRef}`);
  };

  // Handle images page navigation - open to all users
  const handleImagesClick = () => {
    navigate(`/listing/${id}/images`);
  };

  if (loading) {
    return <Spinner />;
  }

  if (!listing) {
    return <div className="text-white text-center py-8">Listing not found.</div>;
  }

  // Check if product is free (price = 0)
  const isFreeProduct = listing.price === 0 || listing.price === '0' || listing.price === 0.0;
  const buttonText = isFreeProduct ? 'Download Zip Product' : 'Buy Zip Product';
  const buttonIcon = isFreeProduct ? <FaDownload className="text-xl" /> : <MdCloudDownload className="text-xl" />;

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Image Section - Open to all users */}
        <div
          className="relative h-[300px] bg-gray-800 rounded-lg overflow-hidden cursor-pointer"
          onClick={handleImagesClick}
        >
          <div className="flex overflow-x-auto snap-x snap-mandatory h-full">
            {listing.imageUrls && listing.imageUrls.length > 0 ? (
              listing.imageUrls.map((url, index) => (
                <img
                  key={index}
                  src={url}
                  alt={`Product image ${index + 1}`}
                  className="h-full w-full object-cover snap-center flex-shrink-0"
                  onError={(e) => (e.target.src = '/default-image.jpg')}
                />
              ))
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gray-700">
                <span className="text-gray-400">No images available</span>
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 bg-white text-black px-2 py-1 rounded-full text-sm flex items-center">
            <FaImages className="mr-1" />
            {listing.imageUrls?.length || 0}
          </div>
        </div>

        {/* Category Badge */}
        <div className="mt-4 flex items-center space-x-2">
          {getCategoryIcon(listing.category)}
          <span className="text-[#4da3ff] font-medium">{listing.category}</span>
          <span className="text-gray-500">•</span>
          <span className="text-gray-400">{listing.subcategory}</span>
        </div>

        {/* Action Buttons with Counts */}
        <div className="mt-4 bg-gray-800 py-2 px-4 rounded-lg flex justify-around items-center">
          <button
            onClick={handleLike}
            className="flex flex-col items-center hover:text-[#FF1493] transition-colors duration-200"
          >
            <GoHeartFill
              size={20}
              className={`${isLiked ? 'text-[#FF1493]' : 'text-white'} ${likeAnimation ? 'animate-bounce' : ''}`}
            />
            <span className="text-xs mt-1">{likeCount}</span>
          </button>
          <button
            onClick={handleComment}
            className="flex flex-col items-center hover:text-orange-500 transition-colors duration-200"
          >
            <BiSolidMessageSquareDetail
              size={20}
              className={`${showComments ? 'text-orange-500' : 'text-white'}`}
            />
            <span className="text-xs mt-1">{commentCount}</span>
          </button>
          <button
            onClick={handleSave}
            className="flex flex-col items-center hover:text-[#1fa9ff] transition-colors duration-200"
          >
            <IoBookmark
              size={20}
              className={`${isSaved ? 'text-[#1fa9ff]' : 'text-white'} ${saveAnimation ? 'animate-bounce' : ''}`}
            />
            <span className="text-xs mt-1">{saveCount}</span>
          </button>
          <button
            onClick={handleShare}
            className="flex flex-col items-center hover:text-orange-500 transition-colors duration-200"
          >
            <FaShare size={20} className="text-white" />
            <span className="text-xs mt-1">Share</span>
          </button>
        </div>

        {/* View Count */}
        <div className="mt-2 px-2 flex items-center space-x-1 text-gray-400 text-sm">
          <SiSimpleanalytics className="text-gray-400" />
          <span className='px-1'>{viewCount} Views</span>
        </div>

        {/* Location and Timestamp */}
        <div className="mt-4 flex items-center space-x-2 text-gray-400">
          <IoLocationSharp />
          <span>{listing.address}, {listing.country}</span>
          <span className="text-sm">• {formatTime(listing.timestamp)}</span>
        </div>

        {/* Title */}
        <h2 className="mt-4 text-2xl font-bold">{listing.title}</h2>

        {/* Price Section */}
        <div className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg inline-block">
          <span className="text-lg font-semibold">
            {listing.currency} {formatPrice(listing.price)}
          </span>
        </div>

        {/* Download/Buy Button (if ZIP file exists) */}
        {listing.zipFileUrl && (
          <div className="mt-4">
            <button
              onClick={handleProductAction}
              disabled={downloading}
              className="w-full bg-gradient-to-r from-red-600 to-red-400 text-white py-4 rounded-xl flex items-center justify-center space-x-3 hover:from-red-700 hover:to-red-500 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <>
                  <Spinner className="h-5 w-5" />
                  <span>Preparing Download...</span>
                </>
              ) : (
                <>
                  {buttonIcon}
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">{buttonText}</span>
                    <span className="text-xs text-blue-200">{listing.zipFileName}</span>
                  </div>
                </>
              )}
            </button>
            
            {/* Purchase Message for Paid Products */}
            {showPurchaseMessage && (
              <div className="relative mt-4 bg-gray-800 border border-gray-700 rounded-lg p-4 pr-12">
                <button
                  onClick={() => setShowPurchaseMessage(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Close message"
                >
                  <FaTimes size={20} />
                </button>
                <p className="text-gray-300">
                  Online purchase is currently unavailable, WhatsApp Seller to buy product.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Contact Buttons */}
        <div className="mt-4 space-y-3">
          <button
            onClick={handleCall}
            className="w-full bg-blue-200 text-blue-800 py-3 rounded-full flex items-center justify-center space-x-2 hover:bg-blue-300 transition-colors duration-200"
          >
            <FaPhone />
            <span>Call Seller</span>
          </button>
          <button
            onClick={handleWhatsApp}
            className="w-full bg-green-500 text-white py-3 rounded-full flex items-center justify-center space-x-2 hover:bg-green-600 transition-colors duration-200"
          >
            <FaWhatsapp />
            <span>WhatsApp Seller</span>
          </button>
          <button
            onClick={handleSourceChat}
            className="w-full bg-[#4da3ff] text-white py-3 rounded-full flex items-center justify-center space-x-2 hover:bg-blue-500 transition-colors duration-200"
          >
            <AiFillMessage />
            <span>Start SourceChat</span>
          </button>
        </div>

        {/* Spacer */}
        <div className="mt-8"></div>

        {/* Product Details Section */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Product Details</h3>
          <div className="space-y-4">
            <div>
              <span className="text-gray-400">Category:</span> {listing.category}
            </div>
            <div>
              <span className="text-gray-400">Subcategory:</span> {listing.subcategory}
            </div>
            <div>
              <span className="text-gray-400">Description:</span>
              <p className="mt-1 whitespace-pre-wrap">{listing.description}</p>
            </div>
            
            {/* Revenue Type for Brand Startups */}
            {listing.category === 'Brand Startups' && listing.revenueType && (
              <>
                <div>
                  <span className="text-gray-400">Revenue Type:</span> {listing.revenueType}
                </div>
                {listing.revenueType === 'Revenue-generating' && (
                  <>
                    <div>
                      <span className="text-gray-400">Annual Revenue:</span> ${formatPrice(listing.annualRevenue || 0)}
                    </div>
                    <div>
                      <span className="text-gray-400">Annual Expense:</span> ${formatPrice(listing.annualExpense || 0)}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Technical Details */}
            {listing.specificFields && Object.keys(listing.specificFields).length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-3 text-[#4da3ff]">Technical Specifications</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(listing.specificFields).map(([key, value]) => {
                    // Format the key name
                    const formattedKey = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase());
                    
                    // Handle arrays (multi-select)
                    const displayValue = Array.isArray(value) ? value.join(', ') : value;
                    
                    return (
                      <div key={key} className="bg-gray-700/50 p-3 rounded-lg">
                        <span className="text-gray-400 text-sm block">{formattedKey}</span>
                        <span className="text-white font-medium">{displayValue}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Seller Profile Section */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleSellerProfile}
              className="w-12 h-12 rounded-full p-[2px] focus:outline-none flex-shrink-0"
              aria-label={`View ${seller?.name || 'Seller'}'s profile`}
            >
              <img
                src={seller?.photoURL || '/default-avatar.jpg'}
                alt={seller?.name || 'Seller'}
                className="w-full h-full rounded-full object-cover border-2 border-gray-900"
                onError={(e) => (e.target.src = '/default-avatar.jpg')}
              />
            </button>

            <div className="flex-1 min-w-0" onClick={handleSellerProfile}>
              <div className="flex items-center">
                <p className="font-semibold text-white truncate">
                  {seller?.name || 'Unknown User'}
                </p>
                {seller?.verified && <VerificationBadge isVerified={seller.verified} />}
              </div>
              <p className="text-gray-400 text-sm truncate">@{seller?.username || 'unknown'}</p>
            </div>

            {/* Follow Button - Only shown to authenticated users */}
            {auth.currentUser && auth.currentUser.uid !== listing.userRef && (
              <button
                onClick={handleFollow}
                disabled={isFollowLoading || !isInitialized || !seller}
                className={`px-4 py-1 rounded-full font-medium transition duration-300 focus:outline-none flex items-center ${
                  followState.className
                } ${isFollowLoading ? 'opacity-70' : ''} flex-shrink-0`}
                aria-label={`${followState.text} ${seller?.name || 'Seller'}`}
              >
                {followState.icon}
                {isFollowLoading ? '...' : followState.text}
              </button>
            )}
          </div>
          <div className="mt-3 flex justify-between text-sm text-gray-400">
            <span>{seller?.followers?.length || 0} followers</span>
            <span className={`${seller?.online ? 'text-green-500' : 'text-gray-500'}`}>
              {seller?.online ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Recommended Listings Section */}
        {similarListings.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">Similar Digital Products</h2>
            <div className="movie-masonry">
              {similarListings.map((similarListing) => (
                <div key={similarListing.id} className="masonry-item">
                  <ListingItem listing={similarListing} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Comment Section Modal */}
      <Modal
        isOpen={showComments}
        onRequestClose={() => setShowComments(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#1a1a1a',
            border: 'none',
            borderRadius: '8px',
            padding: '20px',
            maxHeight: '80vh',
            overflowY: 'auto',
            width: '90%',
            maxWidth: '500px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000,
          },
        }}
        contentLabel="Comment Modal"
      >
        <CommentSection 
          contentId={listing.id} 
          contentType="listing" 
          onNewComment={handleNewComment}
        />
        <button
          onClick={() => setShowComments(false)}
          className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
        >
          Close
        </button>
      </Modal>

      {/* Inject masonry styles */}
      <style>
        {`
          .movie-masonry {
            column-count: 2;
            column-gap: 6px;
            padding: 0 4px;
          }

          @media (min-width: 640px) {
            .movie-masonry {
              column-count: 3;
            }
          }

          @media (min-width: 1024px) {
            .movie-masonry {
              column-count: 4;
            }
          }

          .masonry-item {
            break-inside: avoid;
            margin-bottom: 6px;
          }
        `}
      </style>
    </div>
  );
}